import { useId, type ReactNode } from "react";

import { cn, Label, RadioGroup, RadioGroupItem } from "@sirdab/ui";

export interface ChoiceGroupProps {
  /** The selected value, or null when nothing is chosen yet. */
  value: string | null;
  onChange: (value: string) => void;
  /** Accessible name for the group; the visible heading usually carries it via `aria-labelledby`. */
  "aria-label"?: string | undefined;
  "aria-labelledby"?: string | undefined;
  children: ReactNode;
}

/**
 * The radio list every choice step uses. One `RadioGroup` per question, so arrow keys move
 * between options and Radix follows the page direction with no override.
 */
export function ChoiceGroup({
  value,
  onChange,
  children,
  ...labelling
}: ChoiceGroupProps): React.JSX.Element {
  return (
    <RadioGroup
      value={value ?? ""}
      onValueChange={onChange}
      className="gap-2"
      {...labelling}
    >
      {children}
    </RadioGroup>
  );
}

export interface ChoiceOptionProps {
  value: string;
  checked: boolean;
  /** Already-localised. Either catalogue copy or an API-supplied bilingual label. */
  label: string;
  /** Already-localised supporting line — a fee note, a stock note, a policy statement. */
  description?: ReactNode;
  disabled?: boolean;
  /**
   * Already-localised explanation for why the option is unavailable. Rendered next to the
   * disabled option rather than hiding it, for the same reason ineligible lines stay
   * visible (ADR-007): an option that vanishes generates a support message. It is visible
   * text, not a tooltip, because there is no hover on a phone.
   */
  unavailableReason?: string | undefined;
}

/**
 * One radio in a list. The whole row is the label, so the tap target is the row and not a
 * 16px circle. The control leads in both directions by construction (flex + gap, no
 * physical side). Selection is carried by the ink border, not a tint.
 */
export function ChoiceOption({
  value,
  checked,
  label,
  description,
  disabled = false,
  unavailableReason,
}: ChoiceOptionProps): React.JSX.Element {
  const id = useId();
  return (
    <Label
      htmlFor={id}
      className={cn(
        "min-h-control-touch items-start gap-3 rounded-lg border p-3",
        checked ? "border-foreground" : "border-border",
        disabled && "opacity-50",
      )}
    >
      <RadioGroupItem
        id={id}
        value={value}
        disabled={disabled}
        className="mt-0.5"
      />
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="text-body text-foreground">{label}</span>
        {description !== undefined ? (
          <span className="text-caption-quiet text-muted-foreground">
            {description}
          </span>
        ) : null}
        {unavailableReason !== undefined ? (
          <span className="text-caption-quiet text-muted-foreground">
            {unavailableReason}
          </span>
        ) : null}
      </span>
    </Label>
  );
}
